import React, { useState, useEffect } from 'react';
import { DialogShell } from './ui/DialogShell';
import { Button } from './ui/Button';
import { versionService, Version, UpdateVersionData } from '../services/versionService';
import { VERSION_TYPES, VersionTipo } from '../config/versionTypes';
import { VERSION_AREAS, VersionArea } from '../config/versionAreas';
import { useToast } from './ui/Toast';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { Checkbox } from './ui/Checkbox';

import { parseDateOnly } from '../utils/formatters';
import { TextInput } from './ui/TextInput';
import { Textarea } from './ui/Textarea';
import { Select, SelectOption } from './ui/Select';
import { VersionTypeBadge } from './changelog/VersionTypeBadge';
import { VersionAreaBadge } from './changelog/VersionAreaBadge';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';

interface VersionFormModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly version: Version | null;
}

function formatFechaDisplay(fecha: string): string {
  try {
    const date = parseDateOnly(fecha);
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return fecha;
  }
}

const tipoOptions = Object.keys(VERSION_TYPES).map((key) => ({
  value: key,
  label: VERSION_TYPES[key as VersionTipo].label,
}));

const areaOptions = Object.keys(VERSION_AREAS).map((key) => ({
  value: key,
  label: VERSION_AREAS[key as VersionArea].label,
}));

const renderTipoItem = (option: SelectOption) => (
  <VersionTypeBadge tipo={option.value as VersionTipo} />
);

const renderTipoValue = (option: SelectOption | undefined) =>
  option ? <VersionTypeBadge tipo={option.value as VersionTipo} /> : undefined;

const renderAreaItem = (option: SelectOption) => (
  <VersionAreaBadge area={option.value as VersionArea} />
);

const renderAreaValue = (option: SelectOption | undefined) =>
  option ? <VersionAreaBadge area={option.value as VersionArea} /> : undefined;

export const VersionFormModal: React.FC<VersionFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  version,
}) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const [versionCode, setVersionCode] = useState(() => version?.version_code ?? '');
  const [tipo, setTipo] = useState<VersionTipo>(() => version?.tipo ?? 'Nuevo');
  const [area, setArea] = useState<VersionArea>(() => version?.area ?? 'Experiencia de uso');
  const [esRuptura, setEsRuptura] = useState(() => version?.es_ruptura ?? false);
  const [descripcion, setDescripcion] = useState(() => version?.descripcion ?? '');
  const [fechaDisplay, setFechaDisplay] = useState(() => version ? formatFechaDisplay(version.fecha) : '');

  const { error, clearError, handleError } = useErrorHandler();

  useEffect(() => {
    if (isOpen && version) {
      setVersionCode(version.version_code);
      setTipo(version.tipo);
      setArea(version.area);
      setEsRuptura(version.es_ruptura);
      setDescripcion(version.descripcion);
      setFechaDisplay(formatFechaDisplay(version.fecha));
      clearError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, version]);

  const validateVersionCode = (code: string): boolean => {
    const regex = /^\d+\.\d+(\.\d+)?$/;
    if (!code.trim()) {
      handleError('El código de versión es requerido');
      return false;
    }
    if (!regex.test(code)) {
      handleError('Formato inválido. Use X.X o X.X.X (ej: 1.0 o 1.0.1)');
      return false;
    }
    clearError();
    return true;
  };

  const validateDescripcion = (desc: string): boolean => {
    if (!desc.trim()) {
      handleError('La descripción es requerida');
      return false;
    }
    if (desc.length < 10) {
      handleError('La descripción debe tener al menos 10 caracteres');
      return false;
    }
    if (desc.length > 500) {
      handleError('La descripción no puede exceder 500 caracteres');
      return false;
    }
    clearError();
    return true;
  };

  const handleDescripcionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescripcion(e.target.value);
    validateDescripcion(e.target.value);
  };

  const isFormValid = (): boolean => {
    return validateVersionCode(versionCode) && validateDescripcion(descripcion);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid() || !version) return;

    setLoading(true);
    try {
      const data: UpdateVersionData = { version_code: versionCode, descripcion };
      if (tipo && tipo !== version.tipo) data.tipo = tipo;
      if (area !== version.area) data.area = area;
      if (esRuptura !== version.es_ruptura) data.es_ruptura = esRuptura;
      const { githubUpdated, githubError } = await versionService.updateVersion(version.id, data, version.version_code);
      if (githubUpdated) {
        toast.success(`Versión ${versionCode} actualizada en la app y en GitHub.`);
      } else {
        toast.success(`Versión ${versionCode} actualizada.`);
        if (githubError) toast.error(githubError);
      }
      onSuccess();
      onClose();
    } catch (e) {
      console.error('Error saving version:', e);
      reportarErrorInesperado(e, 'Error al guardar la versión. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogShell isOpen={isOpen} onClose={onClose} title="Editar Versión">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <Select
            label="Tipo"
            options={tipoOptions}
            value={tipo}
            onChange={(v) => {
              const newTipo = v as VersionTipo;
              setTipo(newTipo);
              if (newTipo === 'Removido') setEsRuptura(true);
            }}
            disabled={loading}
            renderItem={renderTipoItem}
            renderValue={renderTipoValue}
          />

          <Select
            label="Área de valor"
            options={areaOptions}
            value={area}
            onChange={(v) => setArea(v as VersionArea)}
            disabled={loading}
            renderItem={renderAreaItem}
            renderValue={renderAreaValue}
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4 items-start">
          <TextInput
            label="Código de Versión"
            value={versionCode}
            onChange={(v) => { setVersionCode(v); validateVersionCode(v); }}
            placeholder="Ej: 1.0 o 1.0.1"
            disabled={loading}
          />

          <Checkbox
            label="Ruptura de compatibilidad"
            description="Marca si este cambio rompe compatibilidad con versiones anteriores"
            checked={esRuptura}
            onChange={setEsRuptura}
            disabled={loading}
          />
        </div>

        <Textarea
          label={`Descripción (${descripcion.length}/500)`}
          value={descripcion}
          onChange={handleDescripcionChange}
          placeholder="Describe los cambios en esta versión..."
          rows={4}
          disabled={loading}
        />

        <div>
          <p className="block text-sm font-semibold text-slate-700 mb-1">Fecha</p>
          <p className="text-sm text-slate-500">{fechaDisplay}</p>
        </div>

        <FormErrorAlert error={error} onClear={clearError} />

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" disabled={loading || !!error} isLoading={loading}>
            Guardar Cambios
          </Button>
        </div>
      </form>
    </DialogShell>
  );
};
